import { Minus, Plus } from "@phosphor-icons/react";
import { motion } from "motion/react";
import { useCallback, useEffect } from "react";

import { useLocale } from "~/hooks/locale";
import { TIME_LIMIT_MAX, TIME_LIMIT_STEP } from "~/utils/const";
import { clockTime } from "~/utils/time";

import { useGame } from "../GameContext";
import type { GameSettings } from "../GameSettings";

export const PreGameSettings: React.FC<{
  minTimeLimit: number;
  onClose: () => void;
}> = ({ onClose, minTimeLimit }) => {
  const { t } = useLocale();
  const { game, setGame } = useGame();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const setGameSettings = useCallback(
    (setting: keyof GameSettings, isEnabled: boolean) => {
      setGame((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          [setting]: isEnabled,
        },
      }));
    },
    [setGame],
  );

  return (
    <motion.div
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: "50%", opacity: 0 }}
      initial={{ y: "50%", opacity: 0 }}
      className="absolute inset-0 grid bg-base-100 max-md:grid-flow-row max-md:auto-rows-fr max-md:divide-y md:auto-cols-fr md:grid-flow-col md:divide-x"
      transition={{
        type: "spring",
        stiffness: 500,
        damping: 40,
      }}
    >
      <div className="flex flex-col items-center justify-center gap-2">
        <h2 className="title title-sm">{t("time_limit")}</h2>
        <div className="flex select-none items-center gap-4">
          <button
            disabled={game.timeLimit <= minTimeLimit}
            className="btn btn-square btn-ghost disabled:bg-transparent"
            onClick={() => {
              setGame((prev) => ({
                ...prev,
                timeLimit: Math.max(
                  prev.timeLimit - TIME_LIMIT_STEP,
                  minTimeLimit,
                ),
              }));
            }}
          >
            <Minus className="size-6" weight="bold" />
          </button>
          <div className="text-6xl tabular-nums heading md:text-7xl">
            {game.timeLimit > 0 ? clockTime(game.timeLimit) : t("none")}
          </div>
          <button
            disabled={game.timeLimit >= TIME_LIMIT_MAX}
            className="btn btn-square btn-ghost disabled:bg-transparent"
            onClick={() => {
              setGame((prev) => ({
                ...prev,
                timeLimit: Math.min(
                  prev.timeLimit + TIME_LIMIT_STEP,
                  TIME_LIMIT_MAX,
                ),
              }));
            }}
          >
            <Plus className="size-6" weight="bold" />
          </button>
        </div>
      </div>
      {game.mode === "classic" && (
        <div className="p-6 md:p-8">
          <label className="label">
            {t("show_first_letter")}
            <input
              type="checkbox"
              className="toggle"
              checked={game.settings.isFirstLetterShown}
              onChange={(event) => {
                setGameSettings("isFirstLetterShown", event.target.checked);
              }}
            />
          </label>
          <label className="label">
            {t("show_typing_feedback")}
            <input
              type="checkbox"
              className="toggle"
              checked={game.settings.isTypingFeedbackShown}
              onChange={(event) => {
                setGameSettings("isTypingFeedbackShown", event.target.checked);
              }}
            />
          </label>
          <label className="label">
            {t("enable_hint")}
            <input
              type="checkbox"
              className="toggle"
              checked={game.settings.isHintEnabled}
              onChange={(event) => {
                setGameSettings("isHintEnabled", event.target.checked);
              }}
            />
          </label>
          <label className="label">
            {t("enable_time_rewards")}
            <input
              type="checkbox"
              className="toggle"
              checked={game.settings.isTimeRewardEnabled}
              onChange={(event) => {
                setGameSettings("isTimeRewardEnabled", event.target.checked);
              }}
            />
          </label>
        </div>
      )}
    </motion.div>
  );
};
